import type { SavedVehicle } from './savedVehiclesService';
import { normalizeVehicleStatus } from '@/utils/vehicleStatus';

export interface RefreshedSavedVehicle {
    vehicle: SavedVehicle['vehicle'];
    availability: string;
    showPrice: boolean;
}

/** Public API reads only; savedAt/originalPrice and the visitor's saved list stay untouched. */
export async function refreshSavedVehicle(saved: SavedVehicle): Promise<RefreshedSavedVehicle> {
    try {
        const response = await fetch(`/api/inventory/vehicles/${encodeURIComponent(saved.vin)}`, { cache: 'no-store', signal: AbortSignal.timeout(20_000) });
        if (!response.ok) throw Object.assign(new Error('Inventory unavailable'), { httpStatus: response.status });
        const current = await response.json();
        if (current.vin !== saved.vin) throw new Error('Inventory VIN mismatch');
        const status = normalizeVehicleStatus(current.status);
        const labels: Record<string, string> = {
            active: 'Available', deposit: 'On Hold', 'sales pending': 'Sale Pending',
            cancelled: 'Sale Pending', 'coming soon': 'Coming Soon', sold: 'Sold', hidden: 'Currently unavailable'
        };
        return {
            vehicle: {
                ...saved.vehicle,
                year: current.year ?? null, make: current.make ?? null, model: current.model ?? null,
                trim: current.trim ?? null, price: current.price ?? null, odometer: current.odometer ?? null,
                image: current.photoUrls?.[0] ?? null, stockId: current.stockId ?? null,
            },
            availability: labels[status ?? ''] ?? 'Contact us for availability',
            showPrice: status === 'active' || status === 'deposit' || status === 'sales pending'
        };
    } catch (error) {
        const missing = typeof error === 'object' && error !== null && 'httpStatus' in error && error.httpStatus === 404;
        return {
            vehicle: saved.vehicle,
            availability: missing ? 'No longer listed' : 'Unable to confirm current availability',
            showPrice: false
        };
    }
}
